/**
 * Tree Validation Utilities
 *
 * Shared validation functions for tree data structures.
 */

/**
 * Validates a tree structure for integrity.
 * Checks for: valid node objects, string IDs, no circular references.
 *
 * @param {Object} node - Root node to validate
 * @param {Function} [onError] - Optional callback for error reporting (msg, node) => void
 * @param {Set} [visited] - Internal tracking for circular reference detection
 * @returns {boolean} True if tree is valid
 */
export function validateTree(node, onError = null, visited = new Set()) {
  if (!node || typeof node !== "object") {
    onError?.("Node is null or not an object", node);
    return false;
  }

  if (!node.id || typeof node.id !== "string") {
    onError?.("Node missing valid id", node);
    return false;
  }

  if (visited.has(node.id)) {
    onError?.(`Circular reference detected at node ${node.id}`, node);
    return false;
  }

  visited.add(node.id);

  if (node.children !== undefined && node.children !== null) {
    if (!Array.isArray(node.children)) {
      onError?.(`Children is not an array at node ${node.id}`, node);
      return false;
    }

    for (const child of node.children) {
      if (!validateTree(child, onError, visited)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Sanitize a CSS color value to prevent injection via style attributes.
 * Only allows hex colors (#rgb, #rrggbb, #rrggbbaa) and a curated set of CSS named colors.
 * Returns fallback for anything else.
 *
 * @param {string} color - Color value to validate
 * @param {string} [fallback="#888"] - Fallback if color is invalid
 * @returns {string} Safe color value
 */
const SAFE_NAMED_COLORS = new Set([
  "red", "blue", "green", "orange", "purple", "pink", "yellow", "cyan",
  "magenta", "white", "black", "gray", "grey", "brown", "teal", "navy",
  "maroon", "olive", "lime", "aqua", "fuchsia", "silver", "transparent",
  "currentcolor", "inherit",
]);

/**
 * Escape a string for safe insertion into an HTML attribute value.
 * Prevents attribute injection via crafted node IDs in shared documents.
 *
 * @param {string} value - Value to escape
 * @returns {string} Escaped value safe for use in quoted HTML attributes
 */
/**
 * Escape a string for safe insertion into HTML content.
 * Prevents XSS via user-controlled text in shared documents.
 */
export function escapeHtml(text) {
  if (!text) return "";
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;");
}

export function escapeAttr(value) {
  if (!value) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function safeColor(color, fallback = "#888") {
  if (!color || typeof color !== "string") return fallback;
  const trimmed = color.trim().toLowerCase();
  if (/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/.test(trimmed)) return trimmed;
  if (SAFE_NAMED_COLORS.has(trimmed)) return trimmed;
  if (/^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/.test(trimmed)) return trimmed;
  if (/^rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*[\d.]+\s*\)$/.test(trimmed)) return trimmed;
  return fallback;
}
